import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  FormControl,
  OutlinedInput,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReusableModal from '../ReusableModal/ReusableModal';
import { generateScenarios } from '../../api/scenarios';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../store/snackBarSlice';

interface GenerateScenariosModalProps {
  open: boolean;
  onClose: () => void;
  workspaceID: string;
  workspaceName: string;
}

const GenerateScenariosModal: React.FC<GenerateScenariosModalProps> = ({
  open,
  onClose,
  workspaceID,
  workspaceName,
}) => {
  const dispatch = useDispatch();
  const [generateResponse, setGenerateResponse] = useState<string | null>(null);

  const handleGenerateScenarios = async () => {
    try {
      await generateScenarios([workspaceID]);
      setGenerateResponse("success");
      dispatch(
        showSnackbar({
          message: "Your request has been queued, check back later once complete",
          severity: "success",
        }),
      );
      setTimeout(() => onClose(), 2000);
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `Failed to generate scenarios: ${error}`,
          severity: "error",
        }),
      );
    }
  };

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title="Generate Scenarios"
      content={
        <Box>
          <Box sx={{mt:2}}>
            <FormControl
              sx={{ width: "800px" }}
              variant="outlined"
            >
              <Typography>Workspace</Typography>
              <OutlinedInput
                value={workspaceName}
                disabled
                fullWidth
              />
            </FormControl>
          </Box>

          <Box sx={{mt:2, mb:2, p:2, backgroundColor: "#f5f7ff", borderRadius: 1, border: "1px solid #e0e4f6"}}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{mb:1}}>About Scenario Generation</Typography>
            <Typography variant="body2">
              This process analyzes contract documents to identify potential value leakage opportunities through a four-step process:
            </Typography>
            <Box component="ul" sx={{mt:1, pl:2}}>
              <Typography component="li" variant="body2">Initial Analysis — Identification of key pages containing potential value leakage issues</Typography>
              <Typography component="li" variant="body2">Extraction — Processing of page numbers and search queries from the initial analysis</Typography>
              <Typography component="li" variant="body2">Detailed Analysis — Comprehensive review of contract terms with reconciliation rationale</Typography>
              <Typography component="li" variant="body2">Hypothetical Scenarios — Creation of specific examples showing financial impact</Typography>
            </Box>
            <Typography variant="body2" sx={{mt:1}}>You will be notified once scenario generation is complete, results will be available in the Scenario Management section once processing is complete.</Typography>
          </Box>

          <Box sx={{mt:2, width: "100%"}}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="scenario-process-content"
                id="scenario-process-header"
              >
                <Typography>Scenario Generation Process</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="step1-content"
                      id="step1-header"
                    >
                      <Typography>Step 1: Initial Analysis</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        multiline
                        fullWidth
                        variant="outlined"
                        value={`You are an expert contract analyst specializing in identifying contract value leakage. 
        
TASK:
You are given a page-by-page contract summary. Your goal is to identify specific pages that may contain information 
indicating potential invoice discrepancies or contract value leakage. These are pages where terms, conditions, 
pricing structures, or obligations might not be properly reflected in invoices.

FOCUS AREAS:
- Pricing terms and rate structures
- Service level agreements (SLAs) and performance metrics
- Volume discounts or tiered pricing
- Renewal terms and price escalation clauses
- Payment terms and conditions
- Scope of work definitions
- Change order procedures
- Termination clauses with financial implications

OUTPUT FORMAT:
Be specific about which pages contain potential value leakage issues. Format your response as:
P2 --> S1: [Brief description of the potential issue on Page 2]
P13,14 --> S2: [Brief description of the potential issue on Pages 13 and 14]

If you believe using RAG (Retrieval Augmented Generation) would be more effective, provide 3-5 specific queries that 
would help retrieve relevant pages. These queries should be focused on contract terms that commonly lead to value leakage.

IMPORTANT: Provide page numbers for further detailed analysis with the original text. Your analysis will guide 
invoice-to-contract reconciliation efforts to identify potential overbilling situations.`}
                        InputProps={{
                          readOnly: true,
                        }}
                        minRows={10}
                        maxRows={10}
                        sx={{
                          '& .MuiInputBase-root': {
                            overflowY: 'auto',
                            maxHeight: '300px'
                          },
                          '& .MuiOutlinedInput-input': {
                            overflowY: 'auto'
                          }
                        }}
                      />
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="step2-content"
                      id="step2-header"
                    >
                      <Typography>Step 2: Extraction</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        multiline
                        fullWidth
                        variant="outlined"
                        value={`TASK:
Extract two specific pieces of information from the provided text:
1. Page numbers mentioned (as integers)
2. Vector retrieval queries (as text strings)

INSTRUCTIONS:
- For page numbers: Extract all mentioned page numbers and format them as a JSON array of integers
- For queries: Extract all suggested search queries and format them as a JSON array of strings
- Separate these two elements with the delimiter " | "

EXPECTED OUTPUT FORMAT:
[1, 2, 5, 10] | ["pricing terms in contract", "service level agreement penalties", "volume discount calculation"]

If you cannot extract one or both elements, use empty arrays: [1, 2] | [] or [] | [] or [] | ["query1"]

TEXT TO ANALYZE:
{text}

RESPONSE (ONLY INCLUDE THE FORMATTED OUTPUT, NO EXPLANATIONS):`}
                        InputProps={{
                          readOnly: true,
                        }}
                        minRows={10}
                        maxRows={10}
                        sx={{
                          '& .MuiInputBase-root': {
                            overflowY: 'auto',
                            maxHeight: '300px'
                          },
                          '& .MuiOutlinedInput-input': {
                            overflowY: 'auto'
                          }
                        }}
                      />
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="step3-content"
                      id="step3-header"
                    >
                      <Typography>Step 3: Detailed Analysis</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        multiline
                        fullWidth
                        variant="outlined"
                        value={`You are a senior contract analyst with expertise in contract value leakage identification. Your analysis will be used by financial teams to recover potential overbilling.

CONTEXT:
You have been provided with:
1. Specific pages with corresponding file_ids from a contract that may contain value leakage indicators
2. Targeted queries highlighting potential areas of concern

TASK:
For each potential value leakage direction identified in these materials, provide a comprehensive analysis including:

1. REASONING FOR RECONCILIATION:
   - Explain specifically why invoice-to-contract reconciliation is necessary
   - Identify the contractual terms that may not be properly reflected in invoices
   - Quantify the potential financial impact where possible (e.g., "A 2% discount on volumes over 1000 units may not be applied")

2. ADDITIONAL INFORMATION NEEDED:
   a) Contract Metadata:
      - Specific contract terms you need (e.g., effective dates, renewal terms)
      - Exact page numbers where this information can be found, or
      - Precise vector search queries to locate this information
      
   b) External Information:
      - Market data, indices, or benchmarks relevant to pricing terms
      - Industry events that might trigger contract clauses
      - Sources where this information can be obtained (invoices, APIs, public data)

FORMAT:
Structure your response with clear headings and bullet points. Number each value leakage direction and provide complete analysis for each. include the file_id and pages for refernces.

FOCUS:
Prioritize high-impact issues where the financial implications for the client are significant. Be specific about contractual language and how it should be interpreted during reconciliation.`}
                        InputProps={{
                          readOnly: true,
                        }}
                        minRows={10}
                        maxRows={10}
                        sx={{
                          '& .MuiInputBase-root': {
                            overflowY: 'auto',
                            maxHeight: '300px'
                          },
                          '& .MuiOutlinedInput-input': {
                            overflowY: 'auto'
                          }
                        }}
                      />
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="step4-content"
                      id="step4-header"
                    >
                      <Typography>Step 4: Hypothetical Scenario</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        multiline
                        fullWidth
                        variant="outlined"
                        value={`TASK:
Based on your analysis of the contract pages and potential value leakage directions, create one or more realistic 
hypothetical scenarios that demonstrate:

1. Specific contract value leakage situations
2. How each leakage benefits the vendor at the client's expense (overbilling)
3. Clear financial impacts that justify investigation

REQUIREMENTS:
- Each scenario should focus on a single, well-defined issue
- Include specific numbers that illustrate the financial impact
- Ensure scenarios represent overbilling (client paying more than they should)
- Make scenarios compelling enough to justify invoice-to-contract reconciliation
- Keep each scenario concise (2-4 sentences maximum)
- Reference specific file_ids and page numbers for each scenario
- You can reference multiple files and pages if a scenario spans across documents

EXAMPLE FORMAT:
"SCENARIO 1: [Specific provision] from file_id=22 (pages 2,7) and file_id=23 (page 4) indicates [condition], 
resulting in [financial impact]. This suggests potential overbilling of approximately [amount]."

"SCENARIO 2: According to file_id=24 (pages 15,16), [specific provision] should apply when [condition], 
resulting in [financial impact]. This indicates possible overbilling of [amount]."

RESPONSE:
Provide numbered scenarios as shown in the example format without additional explanation or context.`}
                        InputProps={{
                          readOnly: true,
                        }}
                        minRows={10}
                        maxRows={10}
                        sx={{
                          '& .MuiInputBase-root': {
                            overflowY: 'auto',
                            maxHeight: '300px'
                          },
                          '& .MuiOutlinedInput-input': {
                            overflowY: 'auto'
                          }
                        }}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>

          {generateResponse === "success" && (
            <Box sx={{mt:2, display: "flex", alignItems: "center"}}>
              <Box sx={{ color: "success.main", mr: 1, display: "flex" }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </Box>
              <Typography color="success.main">
                Your request has been queued, check back later once complete
              </Typography>
            </Box>
          )}
        </Box>
      }
      actions={
        <>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          {generateResponse !== "success" && (
            <Button variant="contained" onClick={handleGenerateScenarios}>
              Generate
            </Button>
          )}
        </>
      }
    />
  );
};

export default GenerateScenariosModal;
